import { Box, Button, Stack, SxProps, Theme, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

const VARIANT_STYLES = {
    default: {
        stackPadding: 3,
        contentPadding: 0,
        imageMinHeight: 160,
        imageBgColor: '',
        contentHeight: '100%',
        buttonVariant: 'contained',
        buttonColor: 'primary',
    },
    outlined: {
        stackPadding: 0,
        contentPadding: 3,
        imageMinHeight: 180,
        imageBgColor: 'cement.200',
        contentHeight: 'auto',
        buttonVariant: 'outlined',
        buttonColor: 'secondary',
    },
} as const;

interface HighlightCardProps {
    /** Main title text */
    title: string;
    /** Description/subtitle text */
    description: string;
    /** Visual variant */
    variant?: 'default' | 'outlined';
    /** Image source path or URL */
    imageSrc?: string;
    /** Action button label */
    actionLabel?: string;
    /** Callback when action button is clicked */
    onAction?: () => void;
    /** Whether the card is in a locked state */
    isLocked?: boolean;
    /** Custom styles */
    sx?: SxProps<Theme>;
}

export default function HighlightCard({
    title,
    description,
    variant = 'default',
    imageSrc,
    actionLabel = 'Access',
    onAction,
    isLocked = false,
    sx,
}: HighlightCardProps) {
    const styles = VARIANT_STYLES[variant];

    return (
        <Stack
            spacing={2}
            sx={{
                height: '100%',
                p: styles.stackPadding,
                backgroundColor: 'common.white',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'transparent',
                overflow: 'hidden',
                ...sx,
            }}>
            {imageSrc ? (
                <Box
                    sx={{
                        minHeight: { xs: 'auto', md: styles.imageMinHeight },
                        maxHeight: { xs: 160, md: 'auto' },
                        backgroundColor: styles.imageBgColor,
                    }}>
                    <Box
                        component="img"
                        src={imageSrc}
                        alt={title}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: { xs: 'contain', md: 'cover' },
                        }}
                    />
                </Box>
            ) : null}
            <Stack p={styles.contentPadding} pt={1} sx={{ height: styles.contentHeight }}>
                <Typography variant="h3">{title}</Typography>
                <Typography variant="body1" color="text.secondary" mt={1}>
                    {description}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {onAction && (
                    <Button
                        sx={{ mt: 3 }}
                        variant={isLocked ? 'outlined' : styles.buttonVariant}
                        color={styles.buttonColor}
                        fullWidth
                        disabled={isLocked}
                        onClick={onAction}
                        startIcon={isLocked ? <LockIcon /> : undefined}>
                        {actionLabel}
                    </Button>
                )}
            </Stack>
        </Stack>
    );
}
